import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TenantCountryCodeEnum } from '../api';
import { useTenant } from '../module/me';

const FILES_BASE = 'https://files.bikeleasing.de/pdf';

export const BikeleasingDocument = {
  AgavAblauf: 'agavAblauf',
  DamageClaim: 'damageClaim',
  LeasableAccessories: 'leasableAccessories',
  OnlineStationaryProcess: 'onlineStationaryProcess',
  BikeDamageProcess: 'bikeDamageProcess',
} as const;

export type BikeleasingDocumentKey = (typeof BikeleasingDocument)[keyof typeof BikeleasingDocument];

type BikeleasingDocumentLocale = 'de' | 'en' | 'at' | 'en-at';

const GERMAN_FALLBACK: Record<BikeleasingDocumentLocale, BikeleasingDocumentLocale> = {
  de: 'de',
  en: 'de',
  at: 'at',
  'en-at': 'at',
};

const DOCUMENTS: Record<BikeleasingDocumentKey, Partial<Record<BikeleasingDocumentLocale, string>>> = {
  agavAblauf: {
    de: 'de/PRO-Portal_DE_AGAV-Allgemeiner-Ablauf_08-2026.pdf',
    en: 'en/PRO-Portal_DE-EN_AGAV-Allgemeiner-Ablauf_08-2026.pdf',
    at: 'at/PRO-Portal_AT_AGAV-Allgemeiner-Ablauf_08-2026.pdf',
    'en-at': 'en-at/PRO-Portal_AT-EN_AGAV-Allgemeiner-Ablauf_08-2026.pdf',
  },
  damageClaim: {
    de: 'de/PRO-Portal_DE_Ablauf-im-Schadensfall_08-2026.pdf',
    en: 'en/PRO-Portal_DE-EN_Ablauf-im-Schadensfall_08-2026.pdf',
    at: 'at/PRO-Portal_AT_Ablauf-im-Schadensfall_08-2026.pdf',
    'en-at': 'en-at/PRO-Portal_AT-EN_Ablauf-im-Schadensfall_08-2026.pdf',
  },
  leasableAccessories: {
    de: 'de/DE_DE_Zubehoer-Leasing_08-2026.pdf',
    at: 'at/AT_DE_Zubehoer-Leasing_08-2026.pdf',
  },
  onlineStationaryProcess: {
    de: 'de/PRO-Portal_DE_Ablauf-Online&Stationaer_08-2026.pdf',
    en: 'en/PRO-Portal_DE_EN_Ablauf-Online&Stationaer_08-2026.pdf',
  },
  bikeDamageProcess: {
    de: 'de/PRO-Portal_DE_Fahrrad-Schaden-Ablauf_08-2026.pdf',
    en: 'en/PRO-Portal_DE-EN_Fahrrad-Schaden-Ablauf_08-2026.pdf',
    at: 'at/PRO-Portal_AT_Fahrrad-Schaden-Ablauf_08-2026.pdf',
  },
};

function toLocale(countryCode?: string, language?: string): BikeleasingDocumentLocale {
  const isAustria = countryCode === TenantCountryCodeEnum.At;
  const isGerman = language?.toLowerCase().startsWith('de') ?? true;

  if (isAustria) {
    return isGerman ? 'at' : 'en-at';
  }
  return isGerman ? 'de' : 'en';
}

export function getBikeleasingDocumentUrl(key: BikeleasingDocumentKey, countryCode?: string, language?: string): string | undefined {
  const requestedLocale = toLocale(countryCode, language);
  const paths = DOCUMENTS[key];
  const resolvedLocale = paths[requestedLocale] ? requestedLocale : GERMAN_FALLBACK[requestedLocale];
  const path = paths[resolvedLocale];

  if (!path) {
    return undefined;
  }

  return `${FILES_BASE}/${path}`;
}

export function useBikeleasingDocumentUrl(key: BikeleasingDocumentKey): string | undefined {
  const { tenant } = useTenant();
  const { i18n } = useTranslation();
  const countryCode = tenant?.countryCode;
  const language = i18n.resolvedLanguage || i18n.language;

  return useMemo(() => getBikeleasingDocumentUrl(key, countryCode, language), [key, countryCode, language]);
}
